<template>
  <div class="backoffice-container">
    <!-- Top Navigation Bar -->
    <v-app-bar app color="white" elevation="1" class="admin-navbar">
      <v-container class="py-0">
        <v-row align="center" no-gutters>
          <v-col>
            <div class="d-flex align-center">
              <v-icon color="primary" size="32" class="mr-3">mdi-shield-crown</v-icon>
              <h1 class="text-h5 font-weight-bold primary--text mb-0">MultiCare Admin</h1>
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn 
              outlined 
              color="primary" 
              @click="logout"
              class="logout-btn"
            >
              <v-icon left>mdi-logout</v-icon>
              Logout
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="admin-main">
      <v-container class="py-8">
        <v-row justify="center">
          <v-col cols="12" xl="10" lg="11">
            <!-- Stats Cards -->
            <v-row class="mb-8">
              <v-col cols="12" sm="6" md="3" lg="2" class="flex-grow-1">
                <v-card class="stats-card" elevation="2">
                  <v-card-text class="text-center">
                    <v-icon color="primary" size="40" class="mb-2">mdi-view-grid</v-icon>
                    <h3 class="text-h4 font-weight-bold primary--text">{{ categories.length }}</h3>
                    <p class="text-subtitle-1 grey--text mb-0">Categories</p>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="2" class="flex-grow-1">
                <v-card class="stats-card" elevation="2">
                  <v-card-text class="text-center">
                    <v-icon color="success" size="40" class="mb-2">mdi-tag-multiple</v-icon>
                    <h3 class="text-h4 font-weight-bold success--text">{{ totalSubcategories }}</h3>
                    <p class="text-subtitle-1 grey--text mb-0">Subcategories</p>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="2" class="flex-grow-1">
                <v-card class="stats-card" elevation="2">
                  <v-card-text class="text-center">
                    <v-icon color="info" size="40" class="mb-2">mdi-cube-outline</v-icon>
                    <h3 class="text-h4 font-weight-bold info--text">{{ totalProducts }}</h3>
                    <p class="text-subtitle-1 grey--text mb-0">Products</p>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="2" class="flex-grow-1">
                <v-card 
                  class="stats-card voter-stat-card" 
                  elevation="2"
                  @click="showVotersDialog = true"
                  ripple
                  title="Click to view voter submissions"
                >
                  <v-card-text class="text-center">
                    <v-icon color="#1e3c72" size="40" class="mb-2">mdi-account-group</v-icon>
                    <h3 class="text-h4 font-weight-bold" style="color: #1e3c72;">{{ votes.length }}</h3>
                    <p class="text-subtitle-1 grey--text mb-0">Total Voters</p>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="2" class="flex-grow-1">
                <v-card class="stats-card" elevation="2">
                  <v-card-text class="text-center">
                    <v-icon color="success" size="40" class="mb-2">mdi-check-circle</v-icon>
                    <h3 class="text-h4 font-weight-bold success--text">{{ systemStatus }}</h3>
                    <p class="text-subtitle-1 grey--text mb-0">System Status</p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Winner Management Section -->
            <v-row class="mb-8">
              <v-col cols="12">
                <v-card class="winner-management-card" elevation="4">
                  <v-card-title class="winner-header">
                    <div class="d-flex align-center">
                      <div class="winner-icon-wrapper mr-4">
                        <v-icon color="white" size="28">mdi-trophy</v-icon>
                      </div>
                      <div>
                        <h2 class="text-h5 font-weight-bold mb-0 text-white">Winner Management</h2>
                        <p class="text-caption text-white-50 mb-0">{{ winners.length }} winners selected</p>
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn 
                      @click="openWinnerDialog"
                      class="select-winner-btn"
                      :disabled="loading"
                      icon
                      large
                      elevation="3"
                    >
                      <v-icon size="28">mdi-plus-circle</v-icon>
                    </v-btn>

                  </v-card-title>

                  <!-- Winners List -->
                  <v-card-text v-if="winners.length > 0" class="pa-6">

                    <!-- Display winners grouped by category -->
                    <div v-for="(categoryWinners, categoryName) in groupedWinners" :key="categoryName" class="mb-8">
                      <!-- Category Header -->
                      <div class="category-winner-header mb-6">
                        <div class="category-header-content">
                          <div class="category-icon-wrapper">
                            <v-icon color="white" size="24">mdi-trophy-variant</v-icon>
                          </div>
                          <div class="category-text">
                            <h3 class="category-title">{{ categoryName }}</h3>
                            <p class="category-subtitle">{{ categoryWinners.length }} winner{{ categoryWinners.length > 1 ? 's' : '' }} selected</p>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Winners for this category -->
                      <v-row>
                        <v-col 
                          v-for="winner in categoryWinners" 
                          :key="winner._id" 
                          cols="12" 
                          sm="6" 
                          md="4" 
                          lg="3"
                        >
                          <v-card class="winner-card" elevation="3">
                            <div class="winner-image-container">
                              <v-img 
                                :src="getWinnerImageUrl(winner)" 
                                height="160" 
                                contain 
                                class="winner-image"
                                :lazy-src="require('@/assets/logo.png')"
                                @error="handleWinnerImageError"
                              >
                                <template v-slot:placeholder>
                                  <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                  </v-row>
                                </template>
                                <template v-slot:error>
                                  <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-icon color="grey" size="48">mdi-image-off</v-icon>
                                  </v-row>
                                </template>
                              </v-img>
                              <div class="winner-badge">
                                <v-icon color="white" size="20">mdi-trophy</v-icon>
                              </div>
                            </div>
                            <v-card-text class="winner-card-content">
                              <h4 class="winner-product-name">{{ winner.productName }}</h4>
                              <p class="winner-subcategory-name">{{ winner.subcategoryName }}</p>
                              
                              <!-- Action Buttons Row -->
                              <div class="action-buttons-row mt-2">
                                <v-btn 
                                  icon 
                                  small 
                                  color="primary" 
                                  @click="openVoteEditDialog(winner)"
                                  class="edit-vote-btn"
                                  title="Edit Vote Data"
                                >
                                  <v-icon size="18">mdi-pencil</v-icon>
                                </v-btn>
                                
                                <v-btn 
                                  icon 
                                  small 
                                  @click="deleteWinner(winner._id)" 
                                  color="error"
                                  class="winner-delete-btn"
                                  title="Remove Winner"
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </div>
                  </v-card-text>

                  <!-- No Winners -->
                  <v-card-text v-else class="text-center pa-6">
                    <v-icon color="grey" size="64" class="mb-4">mdi-trophy-outline</v-icon>
                    <p class="text-h6 grey--text mb-4">No winners selected yet</p>
                    <p class="text-body-2 grey--text mb-4">Select winners from your categories and subcategories</p>
                    <v-btn 
                      color="primary" 
                      @click="openWinnerDialog"
                      outlined
                      :disabled="loading"
                    >
                      <v-icon left>mdi-trophy-plus</v-icon>
                      Select First Winner
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

        <!-- Loading State -->
        <v-row v-if="loading" justify="center" class="my-8">
          <v-col cols="12" class="text-center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4">Loading categories...</p>
          </v-col>
        </v-row>

        <!-- Error State -->
        <v-row v-else-if="error" justify="center" class="my-8">
          <v-col cols="12" class="text-center">
            <v-alert type="error" prominent class="mx-auto" max-width="500">
              {{ error }}
              <v-btn text color="white" @click="fetchCategories" class="ml-4">Retry</v-btn>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Main Content -->
        <div v-else>
          <!-- Categories -->
          <div v-for="category in categories" :key="category._id" class="mb-8">
            <v-card class="category-card" elevation="4">
              <v-card-title class="category-header">
                <div class="d-flex align-center">
                  <div class="category-icon-wrapper mr-4">
                    <v-icon color="white" size="28">mdi-view-grid</v-icon>
                  </div>
                  <div>
                    <h2 class="text-h5 font-weight-bold mb-0 text-white">{{ category.name }}</h2>
                    <p class="text-caption text-white-50 mb-0">{{ category.subcategories ? category.subcategories.length : 0 }} subcategories</p>
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn 
                  icon 
                  @click="addSubcategory(category)" 
                  color="white"
                  class="edit-btn mr-2"
                  title="Add Subcategory"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
                <v-btn 
                  icon 
                  @click="editCategory(category)" 
                  color="white"
                  class="edit-btn"
                  title="Edit Category"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </v-card-title>

              <!-- Subcategories -->
              <div v-if="category.subcategories && category.subcategories.length > 0">
                <div v-for="(subcategory, subIndex) in category.subcategories" :key="subcategory._id || subIndex" class="pa-6">
                  <v-card class="subcategory-card" elevation="2">
                    <v-card-title class="subcategory-header">
                      <div class="d-flex align-center">
                        <div class="subcategory-icon-wrapper mr-3">
                          <v-icon color="success" size="20">mdi-tag-multiple</v-icon>
                        </div>
                        <div>
                          <h3 class="text-h6 font-weight-bold black--text mb-0">{{ subcategory.name }}</h3>
                          <p class="text-caption black--text mb-0">
                            {{ subcategory.products ? subcategory.products.length : 0 }} products
                          </p>
                        </div>
                      </div>
                      <v-spacer></v-spacer>
                      <v-btn 
                        icon 
                        small 
                        @click="editSubcategory(category, subIndex)" 
                        color="primary"
                        class="mr-2 subcategory-btn"
                        title="Edit Subcategory"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn 
                        icon 
                        small 
                        @click="addProduct(category, subIndex)" 
                        color="success"
                        class="mr-2 subcategory-btn"
                        title="Add Product"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                      <v-btn 
                        icon 
                        small 
                        @click="deleteSubcategory(category, subIndex)" 
                        color="error"
                        class="subcategory-btn"
                        title="Delete Subcategory"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-card-title>

                    <!-- Products -->
                    <v-card-text class="pa-4">
                      <v-row>
                        <v-col v-for="(product, prodIndex) in subcategory.products" :key="product._id || prodIndex" cols="12" sm="6" md="4" lg="3">
                          <v-card class="product-card" elevation="3">
                            <div class="product-image-container">
                              <v-img 
                                :src="product.image" 
                                height="160" 
                                contain 
                                class="product-image"
                              ></v-img>
                              <div class="product-overlay">
                                <v-btn 
                                  icon 
                                  small 
                                  @click="editProduct(category, subIndex, prodIndex)" 
                                  color="white"
                                  class="mr-2 product-action-btn"
                                >
                                  <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                                <v-btn 
                                  icon 
                                  small 
                                  @click="deleteProduct(category, subIndex, prodIndex)" 
                                  color="white"
                                  class="product-action-btn"
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </div>
                            <v-card-text class="text-center pa-4">
                              <p class="text-body-1 font-weight-medium product-name">{{ product.name }}</p>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>
              </div>

              <!-- No Subcategories -->
              <v-card-text v-else class="text-center pa-6">
                <v-icon color="grey" size="64" class="mb-4">mdi-folder-open</v-icon>
                <p class="text-h6 grey--text mb-4">No subcategories found</p>
                <v-btn 
                  color="primary" 
                  @click="addSubcategory(category)"
                  outlined
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Subcategory
                </v-btn>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Edit Dialogs -->
    <EditProductDialog 
      v-model="showEditDialog" 
      :product="editingProduct" 
      @save="saveProduct"
      @close="closeEditDialog"
    />

    <EditSubcategoryDialog 
      v-model="showSubcategoryDialog" 
      :subcategory="editingSubcategory" 
      @save="saveSubcategory"
      @close="closeSubcategoryDialog"
    />

    <EditCategoryDialog 
      v-model="showCategoryDialog" 
      :category="editingCategory" 
      @save="saveCategory"
      @close="closeCategoryDialog"
    />

    <WinnerSelectionDialog 
      v-model="showWinnerDialog" 
      :categories="categories"
      @save="onWinnerSaved"
      @close="closeWinnerDialog"
      @error="onWinnerError"
    />

    <!-- Vote Edit Dialog -->
    <v-dialog v-model="showVoteEditDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="vote-dialog-header">
          <v-icon size="24" color="primary" class="mr-3">mdi-chart-line</v-icon>
          Edit Vote Data
          <v-spacer></v-spacer>
          <v-btn icon @click="closeVoteEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div v-if="editingVoteWinner" class="vote-edit-form">
            <!-- Winner Info -->
            <div class="winner-info mb-4">
              <h4 class="winner-name">{{ editingVoteWinner.productName }}</h4>
              <p class="winner-subcategory">{{ editingVoteWinner.subcategoryName }}</p>
            </div>
            
            <!-- Vote Percentage Slider -->
            <div class="vote-percentage-section mb-4">
              <label class="vote-label">Vote Percentage: {{ editingVoteWinner.votePercentage }}%</label>
              <v-slider
                v-model="editingVoteWinner.votePercentage"
                :min="0"
                :max="100"
                :step="1"
                thumb-label
                color="primary"
                track-color="grey lighten-2"
                class="vote-slider"
              ></v-slider>
            </div>
            

            

          </div>
        </v-card-text>
        
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn @click="closeVoteEditDialog" outlined>Cancel</v-btn>
          <v-btn @click="saveVoteData" color="primary" :loading="savingVote">
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Voters Submissions & Results Dialog -->
    <v-dialog v-model="showVotersDialog" max-width="1100" scrollable>
      <v-card class="voter-dialog-card">
        <v-card-title class="dialog-header-blue white--text d-flex align-center py-4 px-6">
          <div class="d-flex align-center">
            <div class="voter-icon-wrapper mr-3">
              <v-icon color="white" size="28">mdi-account-group</v-icon>
            </div>
            <div>
              <h2 class="text-h5 font-weight-bold mb-0 text-white">Voter Submissions & Results</h2>
              <p class="text-caption text-white-50 mb-0">{{ votes.length }} total voter{{ votes.length === 1 ? '' : 's' }} recorded</p>
            </div>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center flex-wrap">
            <v-btn
              color="white"
              outlined
              class="mr-2 export-csv-btn"
              @click="exportVotesToCSV"
              :disabled="votes.length === 0"
            >
              <v-icon left>mdi-download</v-icon>
              Export CSV
            </v-btn>
            <v-btn 
              color="white" 
              icon 
              @click="fetchVotes" 
              :loading="loadingVotes"
              title="Refresh Submissions"
              class="mr-2"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn 
              color="white" 
              icon 
              @click="showVotersDialog = false" 
              title="Close"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Filters and Search Bar -->
          <v-row class="mb-4" align="center">
            <v-col cols="12" md="7">
              <v-text-field
                v-model="voterSearch"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search voter by name or phone..."
                outlined
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="5">
              <v-select
                v-model="genderFilter"
                :items="genderOptions"
                item-text="text"
                item-value="value"
                label="Filter by Gender"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
          </v-row>

          <!-- Voter Submissions Data Table -->
          <v-data-table
            :headers="voterHeaders"
            :items="filteredVotes"
            :items-per-page="10"
            :loading="loadingVotes"
            class="elevation-1 voter-table"
            no-data-text="No voter submissions found"
          >
            <!-- Name column with Gender Icon -->
            <template v-slot:[`item.voterName`]="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="28" :color="item.gender === 'female' ? 'pink lighten-4' : 'blue lighten-4'" class="mr-2">
                  <v-icon size="18" :color="item.gender === 'female' ? 'pink darken-1' : 'blue darken-2'">
                    {{ item.gender === 'female' ? 'mdi-gender-female' : 'mdi-gender-male' }}
                  </v-icon>
                </v-avatar>
                <span class="font-weight-bold">{{ item.voterName }}</span>
              </div>
            </template>

            <!-- Phone column -->
            <template v-slot:[`item.voterPhone`]="{ item }">
              <span class="text-subtitle-2 font-weight-medium">{{ item.voterPhone }}</span>
            </template>

            <!-- Gender chip -->
            <template v-slot:[`item.gender`]="{ item }">
              <v-chip 
                x-small 
                :color="item.gender === 'female' ? 'pink' : 'blue'" 
                text-color="white" 
                class="font-weight-bold text-uppercase"
              >
                {{ item.gender }}
              </v-chip>
            </template>

            <!-- Submitted At -->
            <template v-slot:[`item.submittedAt`]="{ item }">
              <span class="text-caption grey--text text--darken-1">{{ formatDateTime(item.submittedAt) }}</span>
            </template>

            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex align-center justify-end">
                <v-btn
                  small
                  class="mr-2 view-ballot-btn"
                  @click="viewVoteDetails(item)"
                  elevation="1"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  View Details
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDeleteVote(item)"
                  title="Delete Vote"
                  class="delete-vote-btn"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4 bg-grey-light">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showVotersDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Ballot Details Dialog -->
    <v-dialog v-model="showVoteDetailsDialog" max-width="650" scrollable>
      <v-card v-if="selectedVote">
        <v-card-title class="dialog-header-blue white--text">
          <v-icon left color="white">mdi-ballot-recount-outline</v-icon>
          Vote Details - {{ selectedVote.userInfo ? selectedVote.userInfo.name : '' }}
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="showVoteDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6" v-if="selectedVote.userInfo">
          <!-- Voter Summary Banner -->
          <v-card outlined class="pa-4 mb-4 voter-summary-box">
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="text-caption grey--text">Voter Name</div>
                <div class="font-weight-bold text-subtitle-1">{{ selectedVote.userInfo.name }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption grey--text">Phone Number</div>
                <div class="font-weight-bold text-subtitle-1">{{ selectedVote.userInfo.phone }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption grey--text">Gender</div>
                <div class="font-weight-bold text-capitalize">{{ selectedVote.userInfo.gender }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption grey--text">Submitted Date & Time</div>
                <div class="font-weight-medium">{{ formatDateTime(selectedVote.submittedAt) }}</div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Selections List -->
          <h3 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
            Selected Products ({{ selectedVote.votes ? selectedVote.votes.length : 0 }})
          </h3>

          <div class="vote-selections-list">
            <v-card 
              v-for="(v, idx) in selectedVote.votes" 
              :key="idx" 
              outlined 
              class="mb-3 pa-3 vote-item-card"
            >
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption font-weight-bold text-uppercase primary--text">
                  {{ v.categoryName || getCategoryNameById(v.categoryId) || 'Category' }}
                </span>
                <span class="text-caption grey--text" v-if="v.subcategoryName">
                  {{ v.subcategoryName }}
                </span>
              </div>
              <div class="d-flex align-center">
                <v-icon color="success" size="20" class="mr-2">mdi-check-decagram</v-icon>
                <span class="text-subtitle-1 font-weight-bold black--text">{{ v.productName }}</span>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4 bg-grey-light">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showVoteDetailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Vote Confirmation Dialog -->
    <v-dialog v-model="showDeleteVoteDialog" max-width="450">
      <v-card v-if="voteToDelete">
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-alert</v-icon>
          Delete Vote Submission
        </v-card-title>
        <v-card-text class="pa-6" v-if="voteToDelete.userInfo">
          <p class="text-body-1">
            Are you sure you want to delete the vote submitted by 
            <strong>{{ voteToDelete.userInfo.name }}</strong> ({{ voteToDelete.userInfo.phone }})?
          </p>
          <p class="text-caption error--text mb-0">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteVoteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deletingVote" @click="deleteVote">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
    </v-main>
  </div>
</template>

<script>
import EditProductDialog from '@/components/EditProductDialog.vue'
import EditSubcategoryDialog from '@/components/EditSubcategoryDialog.vue'
import EditCategoryDialog from '@/components/EditCategoryDialog.vue'
import WinnerSelectionDialog from '@/components/WinnerSelectionDialog.vue'
import config from '@/config.js'

export default {
  name: 'BackofficeAdmin',
  components: {
    EditProductDialog,
    EditSubcategoryDialog,
    EditCategoryDialog,
    WinnerSelectionDialog
  },
  data() {
    return {
      categories: [],
      winners: [],
      votes: [],
      showVotersDialog: false,
      loadingVotes: false,
      voterSearch: '',
      genderFilter: 'all',
      genderOptions: [
        { text: 'All Genders', value: 'all' },
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' }
      ],
      showVoteDetailsDialog: false,
      selectedVote: null,
      showDeleteVoteDialog: false,
      voteToDelete: null,
      deletingVote: false,
      voterHeaders: [
        { text: '#', value: 'index', sortable: false, width: '50px' },
        { text: 'Voter Name', value: 'voterName' },
        { text: 'Phone Number', value: 'voterPhone' },
        { text: 'Gender', value: 'gender', width: '100px' },
        { text: 'Submitted At', value: 'submittedAt', width: '220px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right', width: '170px' }
      ],
      loading: true,
      error: null,
      showEditDialog: false,
      showSubcategoryDialog: false,
      showCategoryDialog: false,
      showWinnerDialog: false,
      showVoteEditDialog: false,
      editingProduct: null,
      editingSubcategory: null,
      editingCategory: null,
      editingVoteWinner: null,
      savingVote: false,
      editingIndexes: { categoryIndex: -1, subcategoryIndex: -1, productIndex: -1 }
    }
  },
  computed: {
    totalSubcategories() {
      return this.categories.reduce((total, category) => {
        return total + (category.subcategories ? category.subcategories.length : 0)
      }, 0)
    },
    totalProducts() {
      return this.categories.reduce((total, category) => {
        if (!category.subcategories) return total
        return total + category.subcategories.reduce((subTotal, subcategory) => {
          return subTotal + (subcategory.products ? subcategory.products.length : 0)
        }, 0)
      }, 0)
    },
    systemStatus() {
      return 'Online'
    },
    groupedWinners() {
      // Group winners by category
      const grouped = {}
      this.winners.forEach(winner => {
        if (!grouped[winner.categoryName]) {
          grouped[winner.categoryName] = []
        }
        grouped[winner.categoryName].push(winner)
      })
      return grouped
    },
    totalVotePercentage() {
      if (this.winners.length === 0) return 0
      const total = this.winners.reduce((sum, winner) => sum + (winner.votePercentage || 0), 0)
      return Math.round(total / this.winners.length)
    },
    filteredVotes() {
      if (!this.votes) return []
      return this.votes
        .map((v, i) => ({
          ...v,
          index: i + 1,
          voterName: v.userInfo ? v.userInfo.name : '',
          voterPhone: v.userInfo ? v.userInfo.phone : '',
          gender: v.userInfo ? v.userInfo.gender : '',
          votesCount: v.votes ? v.votes.length : 0
        }))
        .filter(vote => {
          // Robust Gender filter (case-insensitive)
          if (this.genderFilter && this.genderFilter.toLowerCase() !== 'all') {
            const voterGender = (vote.gender || '').toLowerCase().trim()
            const targetGender = this.genderFilter.toLowerCase().trim()
            if (voterGender !== targetGender) {
              return false
            }
          }
          // Search filter (case-insensitive)
          if (this.voterSearch && this.voterSearch.trim()) {
            const q = this.voterSearch.toLowerCase().trim()
            const name = (vote.voterName || '').toLowerCase()
            const phone = (vote.voterPhone || '').toLowerCase()
            return name.includes(q) || phone.includes(q)
          }
          return true
        })
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchCategories(),
      this.fetchWinners(),
      this.fetchVotes()
    ])
  },
  methods: {
    async fetchVotes() {
      try {
        this.loadingVotes = true
        const response = await fetch(`${config.API_BASE_URL}/votes`)
        if (!response.ok) throw new Error('Failed to fetch votes')
        this.votes = await response.json()
      } catch (error) {
        console.error('Error fetching votes:', error)
      } finally {
        this.loadingVotes = false
      }
    },

    viewVoteDetails(vote) {
      this.selectedVote = vote
      this.showVoteDetailsDialog = true
    },

    confirmDeleteVote(vote) {
      this.voteToDelete = vote
      this.showDeleteVoteDialog = true
    },

    async deleteVote() {
      if (!this.voteToDelete) return
      try {
        this.deletingVote = true
        const response = await fetch(`${config.API_BASE_URL}/votes/${this.voteToDelete._id}`, {
          method: 'DELETE'
        })
        if (!response.ok) throw new Error('Failed to delete vote')
        this.votes = this.votes.filter(v => v._id !== this.voteToDelete._id)
        this.showDeleteVoteDialog = false
        this.voteToDelete = null
      } catch (error) {
        console.error('Error deleting vote:', error)
        alert('Failed to delete vote: ' + error.message)
      } finally {
        this.deletingVote = false
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('en-MY', {
        timeZone: 'Asia/Kuala_Lumpur',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      })
    },

    getCategoryNameById(id) {
      const cat = this.categories.find(c => c._id === id)
      return cat ? cat.name : ''
    },

    exportVotesToCSV() {
      if (!this.votes || this.votes.length === 0) return

      const headers = ['No', 'Voter Name', 'Phone Number', 'Gender', 'Submitted At (MYT)']
      const rows = this.votes.map((vote, idx) => [
        idx + 1,
        `"${(vote.userInfo?.name || '').replace(/"/g, '""')}"`,
        `="${vote.userInfo?.phone || ''}"`,
        `"${(vote.userInfo?.gender || '').toUpperCase()}"`,
        `"${this.formatDateTime(vote.submittedAt)}"`
      ])

      const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n')
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `voters_list_${new Date().toISOString().slice(0, 10)}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async fetchCategories() {
      try {
        this.loading = true
        this.error = null
        const response = await fetch(`${config.API_BASE_URL}/categories`)
        if (!response.ok) throw new Error('Failed to fetch categories')
        this.categories = await response.json()
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },

    editProduct(category, subIndex, prodIndex) {
      this.editingProduct = { ...category.subcategories[subIndex].products[prodIndex] }
      this.editingIndexes = { categoryIndex: this.categories.indexOf(category), subcategoryIndex: subIndex, productIndex: prodIndex }
      this.showEditDialog = true
    },

    editSubcategory(category, subIndex) {
      this.editingSubcategory = { ...category.subcategories[subIndex] }
      this.editingIndexes = { categoryIndex: this.categories.indexOf(category), subcategoryIndex: subIndex }
      this.showSubcategoryDialog = true
    },

    editCategory(category) {
      this.editingCategory = { ...category }
      this.editingIndexes = { categoryIndex: this.categories.indexOf(category) }
      this.showCategoryDialog = true
    },

    addProduct(category, subIndex) {
      this.editingProduct = { name: '', image: '/lucky.png' }
      this.editingIndexes = { categoryIndex: this.categories.indexOf(category), subcategoryIndex: subIndex, productIndex: -1 }
      this.showEditDialog = true
    },

    addSubcategory(category) {
      this.editingSubcategory = { name: '', products: [] }
      this.editingIndexes = { categoryIndex: this.categories.indexOf(category), subcategoryIndex: -1 }
      this.showSubcategoryDialog = true
    },



    async saveProduct(product) {
      try {
        const { categoryIndex, subcategoryIndex, productIndex } = this.editingIndexes
        const category = this.categories[categoryIndex]
        
        // Ensure subcategories array exists
        if (!category.subcategories) {
          category.subcategories = []
        }
        
        // Ensure the specific subcategory exists
        if (!category.subcategories[subcategoryIndex]) {
          category.subcategories[subcategoryIndex] = { name: '', products: [] }
        }
        
        // Ensure image is set
        if (!product.image) {
          product.image = '/lucky.png'
        }
        
        if (productIndex >= 0) {
          // Update existing product
          category.subcategories[subcategoryIndex].products[productIndex] = product
        } else {
          // Add new product
          category.subcategories[subcategoryIndex].products.push(product)
        }

        await this.updateCategory(category)
        this.closeEditDialog()
      } catch (error) {
        console.error('Error saving product:', error)
      }
    },

    async saveSubcategory(subcategory) {
      try {
        const { categoryIndex, subcategoryIndex } = this.editingIndexes
        const category = this.categories[categoryIndex]
        
        // Ensure subcategories array exists
        if (!category.subcategories) {
          category.subcategories = []
        }
        
        if (subcategoryIndex >= 0) {
          // Update existing subcategory
          category.subcategories[subcategoryIndex] = subcategory
        } else {
          // Add new subcategory
          category.subcategories.push(subcategory)
        }

        await this.updateCategory(category)
        this.closeSubcategoryDialog()
      } catch (error) {
        console.error('Error saving subcategory:', error)
      }
    },

    async saveCategory(category) {
      try {
        const { categoryIndex } = this.editingIndexes
        this.categories[categoryIndex] = category
        await this.updateCategory(category)
        this.closeCategoryDialog()
      } catch (error) {
        console.error('Error saving category:', error)
      }
    },

    async deleteProduct(category, subIndex, prodIndex) {
      if (confirm('Are you sure you want to delete this product?')) {
        try {
          category.subcategories[subIndex].products.splice(prodIndex, 1)
          await this.updateCategory(category)
        } catch (error) {
          console.error('Error deleting product:', error)
        }
      }
    },

    async deleteSubcategory(category, subIndex) {
      if (confirm('Are you sure you want to delete this subcategory? This will also delete all products in this subcategory.')) {
        try {
          category.subcategories.splice(subIndex, 1)
          await this.updateCategory(category)
        } catch (error) {
          console.error('Error deleting subcategory:', error)
        }
      }
    },

    async updateCategory(category) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/categories/${category._id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(category)
        })
        
        if (!response.ok) throw new Error('Failed to update category')
        
        // Update local state
        const index = this.categories.findIndex(c => c._id === category._id)
        if (index >= 0) {
          this.categories[index] = category
        }
      } catch (error) {
        console.error('Error updating category:', error)
        throw error
      }
    },

    closeEditDialog() {
      this.showEditDialog = false
      this.editingProduct = {}
      this.editingIndexes = { categoryIndex: -1, subcategoryIndex: -1, productIndex: -1 }
    },

    closeSubcategoryDialog() {
      this.showSubcategoryDialog = false
      this.editingSubcategory = {}
      this.editingIndexes = { categoryIndex: -1, subcategoryIndex: -1 }
    },

    closeCategoryDialog() {
      this.showCategoryDialog = false
      this.editingCategory = {}
      this.editingIndexes = { categoryIndex: -1 }
    },

    async fetchWinners() {
      try {
        const response = await fetch(`${config.API_BASE_URL}/winners`)
        if (!response.ok) throw new Error('Failed to fetch winners')
        this.winners = await response.json()
        
        // Ensure winners have vote percentage fields initialized
        this.winners = this.winners.map(winner => ({
          ...winner,
          votePercentage: winner.votePercentage || 25,
          voteCount: winner.voteCount || 50,
          totalVotes: winner.totalVotes || 200
        }))
        

      } catch (error) {
        console.error('Error fetching winners:', error)
        // Don't show error for winners as it's not critical
      }
    },

    openWinnerDialog() {
      this.showWinnerDialog = true
    },

    closeWinnerDialog() {
      this.showWinnerDialog = false
    },

    onWinnerSaved(winner) {
      this.winners.unshift(winner)
      this.$emit('winner-saved', winner)
    },

    onWinnerError(error) {
      console.error('Winner selection error:', error)
      // You could show a toast notification here
    },

    getWinnerImageUrl(winner) {
      if (!winner.productImage) {
        return require('@/assets/logo.png')
      }
      
      // Decode HTML entities in the URL
      let imageUrl = winner.productImage
      if (typeof imageUrl === 'string') {
        // Decode HTML entities like &#x2F; to /
        imageUrl = imageUrl.replace(/&#x2F;/g, '/')
                          .replace(/&#x2f;/g, '/')
                          .replace(/&amp;/g, '&')
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>')
                          .replace(/&quot;/g, '"')
                          .replace(/&#x27;/g, "'")
      }
      
      // If it's already a full URL (Cloudinary), return as is
      if (imageUrl.startsWith('http')) {
        return imageUrl
      }
      
      // If it's a base64 image, return as is
      if (imageUrl.startsWith('data:image/')) {
        return imageUrl
      }
      
      // If it's a local path, try to construct the full URL
      if (imageUrl.startsWith('/')) {
        return `${config.API_BASE_URL}${imageUrl}`
      }
      
      // If it's just a filename, try to construct the full URL
      return `${config.API_BASE_URL}/images/${imageUrl}`
    },

    handleWinnerImageError(event) {
      console.warn('Failed to load winner image:', event)
      // The error template will handle the display, no need to hide manually
    },

    async deleteWinner(winnerId) {
      if (confirm('Are you sure you want to remove this winner?')) {
        try {
          const response = await fetch(`${config.API_BASE_URL}/winners/${winnerId}`, {
            method: 'DELETE'
          })
          
          if (!response.ok) throw new Error('Failed to delete winner')
          
          // Remove from local state
          this.winners = this.winners.filter(winner => winner._id !== winnerId)
        } catch (error) {
          console.error('Error deleting winner:', error)
        }
      }
    },

    async updateWinnerVotePercentage(winner) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/winners/${winner._id}/vote-percentages`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            votePercentage: winner.votePercentage,
            voteCount: winner.voteCount,
            totalVotes: winner.totalVotes
          })
        })
        
        if (!response.ok) {
          throw new Error('Failed to update vote percentages')
        }
        
        const updatedWinner = await response.json()
        
        // Update local state
        const index = this.winners.findIndex(w => w._id === winner._id)
        if (index >= 0) {
          this.winners[index] = updatedWinner
        }
        
        console.log('Vote percentages updated successfully')
      } catch (error) {
        console.error('Error updating vote percentages:', error)
        // You could show a toast notification here
      }
    },



    openVoteEditDialog(winner) {
      this.editingVoteWinner = { ...winner }
      this.showVoteEditDialog = true
    },

    closeVoteEditDialog() {
      this.showVoteEditDialog = false
      this.editingVoteWinner = null
    },

    async saveVoteData() {
      try {
        this.savingVote = true
        
        const response = await fetch(`${config.API_BASE_URL}/winners/${this.editingVoteWinner._id}/vote-percentages`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            votePercentage: this.editingVoteWinner.votePercentage
          })
        })
        
        if (!response.ok) {
          throw new Error('Failed to update vote percentages')
        }
        
        const updatedWinner = await response.json()
        
        // Update local state
        const index = this.winners.findIndex(w => w._id === this.editingVoteWinner._id)
        if (index >= 0) {
          this.winners[index] = updatedWinner
        }
        
        this.closeVoteEditDialog()
        console.log('Vote data saved successfully')
      } catch (error) {
        console.error('Error saving vote data:', error)
        // You could show a toast notification here
      } finally {
        this.savingVote = false
      }
    },

    logout() {
      localStorage.removeItem('admin_authenticated')
      localStorage.removeItem('admin_timestamp')
      this.$router.push('/admin-login')
    }
  }
}
</script>

<style scoped>
.backoffice-container {
  min-height: 100vh;
}

.admin-navbar {
  border-bottom: 1px solid #e0e0e0;
}

.logout-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.admin-main {
  padding-top: 50px !important;
}

.stats-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.category-card {
  border-radius: 20px;
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
}

.category-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px 28px;
  position: relative;
  overflow: hidden;
}

.category-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.category-icon-wrapper {
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.edit-btn {
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  background: rgba(255,255,255,0.25);
  transform: scale(1.05);
}

.subcategory-card {
  border-radius: 16px;
  border: none;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  overflow: hidden;
}

.subcategory-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 25px rgba(0,0,0,0.12);
}

.subcategory-header {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-bottom: 1px solid #90caf9;
  padding: 20px 24px;
  position: relative;
}

.subcategory-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%);
  pointer-events: none;
}

.subcategory-icon-wrapper {
  background: transparent;
  border-radius: 10px;
  padding: 10px;
}

.subcategory-btn {
  background: transparent;
  backdrop-filter: blur(10px);
  border-radius: 6px;
  transition: all 0.3s ease;
}

.subcategory-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.product-card {
  border-radius: 16px;
  transition: all 0.3s ease;
  border: none;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.product-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.15);
}

.product-image-container {
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow: hidden;
}

.product-image {
  transition: all 0.3s ease;
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.product-card:hover .product-overlay {
  opacity: 1;
}

.product-action-btn {
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.product-action-btn:hover {
  background: rgba(255,255,255,0.3);
  transform: scale(1.1);
}

.product-name {
  color: #2c3e50;
  line-height: 1.4;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

/* Loading and Error States */
.loading-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Winner Management Styles */
.winner-management-card {
  border-radius: 20px;
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.winner-management-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
}

.winner-header {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  padding: 24px 28px;
  position: relative;
  overflow: hidden;
}

.winner-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.winner-icon-wrapper {
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.select-winner-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.select-winner-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

.select-winner-btn:active {
  transform: translateY(0) scale(0.95);
}

.select-winner-btn:disabled {
  background: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Category Winner Headers */
.category-winner-header {
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.15);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
}

.category-winner-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
  pointer-events: none;
}

.category-header-content {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.category-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  margin-right: 16px;
  backdrop-filter: blur(10px);
}

.category-text {
  flex: 1;
}

.category-title {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.winner-card {
  border-radius: 16px;
  transition: all 0.3s ease;
  border: none;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  height: 360px;
  display: flex;
  flex-direction: column;
}

.winner-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.15);
}

.winner-image-container {
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow: hidden;
  height: 160px;
  flex-shrink: 0;
}

.winner-image {
  transition: all 0.3s ease;
  width: 100%;
  height: 100%;
}

.winner-card:hover .winner-image {
  transform: scale(1.05);
}

.winner-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.winner-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 16px 20px 16px !important;
  text-align: center;
}

.winner-product-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1976d2;
  margin: 0 0 8px 0;
  line-height: 1.3;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.winner-subcategory-name {
  font-size: 0.85rem;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.2;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.winner-delete-btn {
  margin-top: auto;
}

/* Simple Vote Percentage Editor */
.simple-vote-editor {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.vote-editor-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
}

.vote-editor-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
}

.vote-percentage-row {
  margin-bottom: 16px;
}

.vote-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.vote-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.vote-range-slider {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #dee2e6;
  outline: none;
  -webkit-appearance: none;
}

.vote-range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.vote-range-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.vote-percentage-display {
  min-width: 60px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: #007bff;
  background: white;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.vote-count-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.vote-count-group {
  flex: 1;
}

.vote-number-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}

.vote-number-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

.save-vote-btn {
  width: 100%;
  margin-top: 8px;
}

.debug-info {
  font-family: monospace;
  font-size: 0.8rem;
}

/* Vote Edit Dialog Styles */
.vote-dialog-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 8px 8px 0 0;
}

.vote-edit-form {
  max-width: 100%;
}

.winner-info {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.winner-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1976d2;
  margin: 0 0 8px 0;
}

.winner-subcategory {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}

.vote-percentage-section {
  margin-bottom: 24px;
}

.vote-label {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.vote-slider {
  margin-top: 8px;
}

.vote-input {
  margin-bottom: 8px;
}

.vote-preview {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.preview-bar {
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
}

.preview-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.preview-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.action-buttons-row {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 8px;
}

.edit-vote-btn {
  /* No additional margins needed */
}



/* Voter Submissions & Results Card Styles */
.voter-management-card {
  border-radius: 20px;
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  background: #ffffff;
}

.voter-management-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.voter-header {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
  padding: 24px 28px;
  position: relative;
  overflow: hidden;
}

.voter-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 100%);
  pointer-events: none;
}

.voter-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.export-csv-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  color: white !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  transition: all 0.25s ease !important;
}

.export-csv-btn:hover {
  background: rgba(255, 255, 255, 0.25) !important;
}

.voter-table {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.voter-table th {
  font-weight: 700 !important;
  color: #334155 !important;
  background: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.8rem !important;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0 !important;
}

.voter-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.view-ballot-btn {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-size: 0.85rem !important;
  box-shadow: 0 2px 6px rgba(30, 60, 114, 0.2) !important;
  transition: all 0.25s ease !important;
}

.view-ballot-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 10px rgba(30, 60, 114, 0.3) !important;
}

.voter-stat-card {
  cursor: pointer !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  user-select: none;
}

.voter-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(30, 60, 114, 0.18) !important;
}

.voter-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.delete-vote-btn {
  transition: all 0.2s ease;
}

.delete-vote-btn:hover {
  background: rgba(244, 67, 54, 0.1) !important;
  transform: scale(1.1);
}

.dialog-header-blue {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 20px 24px;
  font-weight: 700;
  font-size: 1.2rem;
}

.voter-summary-box {
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.vote-item-card {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #1e3c72 !important;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
}

.vote-item-card:hover {
  border-color: #cbd5e1;
  border-left-color: #2a5298 !important;
  transform: translateX(3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.bg-grey-light {
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .admin-main {
    padding-top: 50px !important;
  }
  
  .stats-card {
    margin-bottom: 16px;
  }
}



@media (max-width: 600px) {
  .category-header {
    padding: 16px 20px;
  }
  
  .subcategory-header {
    padding: 12px 16px;
  }
  
  .product-card {
    margin-bottom: 16px;
  }
}
</style> 